import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { APPLICATION_BREAKPOINTS, Button, useCheckBreakpoint } from '@marriott/mi-ui-library';

import { StyledTabNavigation, StyledTabbedWrapper } from './Tabs.styles';
import { TabNavigationListProps } from './Tabs.types';
import { POLARIS_CONSTANTS } from '../../utils/constants';

export const TAB_CENTER_SCROLL_MS = 300;

export const TabNavigation: React.FC<TabNavigationListProps> = props => {
  const {
    tabNavigationList,
    selectedTab,
    setSelectedTab,
    children,
    pageTitle,
    isNextGenSearchForm = false,
    isConvoAIActive = false,
  } = props;
  const navigationRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const titleTextRef = useRef<HTMLSpanElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const centerScrollRafRef = useRef<number | undefined>(undefined);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
  const isViewportSAndAbove = useCheckBreakpoint('viewportS');
  const isMobileViewport = !isViewportSAndAbove;
  const { POLARIS_TAB_TITLE_ID, SEARCH_FOCUS_EVENT, SEARCH_BLUR_EVENT } = POLARIS_CONSTANTS;

  const checkOverflow = () => {
    if (!navigationRef.current || !containerRef.current) return;
    const { scrollWidth, clientWidth, scrollLeft } = navigationRef.current;
    setShowLeftArrow(scrollLeft > 0);
    /*
    When scroll is at very right end.
    Math.abs() < 2 to allows for tiny rounding or fractional differences
    that can occur across browsers or OS.
   */
    const isAtRightEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) < 2;
    const showRightArrow = isAtRightEnd ? false : scrollLeft + clientWidth < scrollWidth;
    setShowRightArrow(showRightArrow);
  };

  useEffect(() => {
    const handleInitialCheck = () => {
      requestAnimationFrame(checkOverflow);
    };
    handleInitialCheck();
    window.addEventListener('resize', checkOverflow);
    navigationRef.current?.addEventListener('scroll', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    if (navigationRef.current) {
      navigationRef.current.addEventListener('scroll', checkOverflow);
    }
    return () => {
      window.removeEventListener('resize', checkOverflow);
      if (navigationRef.current) {
        navigationRef.current.removeEventListener('scroll', checkOverflow);
      }
    };
  }, []);

  useEffect(() => {
    const titleEl = document.getElementById(POLARIS_TAB_TITLE_ID);
    if (!titleEl) return;

    const onSearchFocus = () => {
      titleEl.setAttribute('aria-hidden', 'false');
    };
    const onSearchBlur = () => {
      titleEl.setAttribute('aria-hidden', 'true');
    };
    window.addEventListener(SEARCH_FOCUS_EVENT, onSearchFocus);
    window.addEventListener(SEARCH_BLUR_EVENT, onSearchBlur);

    titleEl.setAttribute('aria-hidden', 'true');
    return () => {
      window.removeEventListener(SEARCH_FOCUS_EVENT, onSearchFocus);
      window.removeEventListener(SEARCH_BLUR_EVENT, onSearchBlur);
    };
  }, [POLARIS_TAB_TITLE_ID]);

  useEffect(() => {
    if (!titleTextRef.current || typeof window === 'undefined') return;

    const titleTextEl = titleTextRef.current;
    const rootEl = document.documentElement;

    const updateWrapOffset = () => {
      if (!isMobileViewport) {
        rootEl.style.setProperty('--tab-title-wrap-offset', '0px');
        return;
      }

      const lineRects = Array.from(titleTextEl.getClientRects());
      const distinctLineRects = Array.from(new Map(lineRects.map(rect => [Math.round(rect.top), rect])).values());

      if (distinctLineRects.length < 2) {
        rootEl.style.setProperty('--tab-title-wrap-offset', '0px');
        return;
      }

      const wrappedLinesOffset = distinctLineRects
        .slice(1)
        .reduce((totalHeight, rect) => totalHeight + Math.ceil(rect.height), 0);
      rootEl.style.setProperty('--tab-title-wrap-offset', `${wrappedLinesOffset}px`);
    };

    updateWrapOffset();

    const resizeObserver = new ResizeObserver(() => requestAnimationFrame(updateWrapOffset));
    resizeObserver.observe(titleTextEl);
    window.addEventListener('resize', updateWrapOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWrapOffset);
      rootEl.style.setProperty('--tab-title-wrap-offset', '0px');
    };
  }, [isMobileViewport]);

  const handleScroll = (direction: string) => {
    if (!navigationRef.current) return;

    const container = navigationRef.current;
    const currentScroll = container.scrollLeft;
    // to add padding of the clientWidth to the scroll amount for right scroll and subtract for left scroll
    const sidePaddingAmount = 18;
    // Find all tab elements
    const tabs = Object.values(tabRefs.current).filter((tab): tab is HTMLButtonElement => !!tab);
    const visibleRight = currentScroll + container.clientWidth;
    if (tabs.length === 0) return;

    if (direction === 'right') {
      // first tab that ends beyond the visible right edge (partially or fully hidden)
      const nextTab = tabs.find(tab => tab.offsetLeft + tab.offsetWidth + sidePaddingAmount > visibleRight);
      if (nextTab) {
        // align nextTab's RIGHT edge with the container's right edge
        const desired = nextTab.offsetLeft + nextTab.offsetWidth - container.clientWidth;
        // clamp between 0 and max scrollLeft
        const maxScroll = container.scrollWidth - container.clientWidth;
        const target = Math.max(0, Math.min(desired, maxScroll));
        container.scrollTo({ left: target + sidePaddingAmount, behavior: 'smooth' });
      } else {
        // no next tab (we're at/near the end) -> ensure we scroll to the end
        container.scrollTo({ left: container.scrollWidth - container.clientWidth, behavior: 'smooth' });
      }
    } else {
      // left: find the first tab whose left is before currentScroll (i.e., hidden on the left)
      const prevTab = [...tabs].reverse().find(tab => tab.offsetLeft < currentScroll);
      if (prevTab) {
        // align that tab's LEFT edge with the container's left edge
        const scrollAmountLeft = prevTab.offsetLeft - sidePaddingAmount;
        const target = Math.max(0, scrollAmountLeft);

        container.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        // already at the start
        container.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }
  };

  // Desktop starts at viewportL (992px). Below that, overflowing tabs should
  // bring the selected tab to the center of the scroller on tap (300ms).
  const desktopMinWidth = APPLICATION_BREAKPOINTS?.['viewportL'] ?? 992;
  const scrollTabToCenter = useCallback(
    (tabEl: HTMLButtonElement | null) => {
      const container = navigationRef.current;
      if (
        !container ||
        !tabEl ||
        typeof window === 'undefined' ||
        window.innerWidth >= desktopMinWidth ||
        container.scrollWidth <= container.clientWidth
      )
        return;

      const targetScrollLeft = Math.max(
        0,
        Math.min(
          tabEl.offsetLeft + tabEl.offsetWidth / 2 - container.clientWidth / 2,
          container.scrollWidth - container.clientWidth
        )
      );
      const startScrollLeft = container.scrollLeft;
      const distance = targetScrollLeft - startScrollLeft;
      if (Math.abs(distance) < 1) return;

      if (centerScrollRafRef.current) {
        window.cancelAnimationFrame(centerScrollRafRef.current);
      }

      // CSS `scroll-behavior: smooth` would fight a timed rAF animation.
      const previousScrollBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = 'auto';

      let startTime: number | null = null;
      const step = (now: number) => {
        if (startTime === null) startTime = now;
        const progress = Math.min((now - startTime) / TAB_CENTER_SCROLL_MS, 1);
        container.scrollLeft = startScrollLeft + distance * progress;
        if (progress < 1) {
          centerScrollRafRef.current = window.requestAnimationFrame(step);
          return;
        }
        container.style.scrollBehavior = previousScrollBehavior;
        centerScrollRafRef.current = undefined;
      };

      centerScrollRafRef.current = window.requestAnimationFrame(step);
    },
    [desktopMinWidth]
  );

  useEffect(
    () => () => {
      if (centerScrollRafRef.current) {
        window.cancelAnimationFrame(centerScrollRafRef.current);
      }
    },
    []
  );

  // - If selectedTab equals a tabValue, parent uses value-mode (TabbedLayout)
  // - Otherwise assume title-mode (DynamicTabbedLayout)
  const isValueMode = tabNavigationList.some(t => t.tabValue === selectedTab);

  useEffect(() => {
    const matchedTab = tabNavigationList.find(tab => tab.tabValue === selectedTab || tab.tabTitle === selectedTab);
    const tabKey = matchedTab?.tabValue;
    if (!tabKey) return;
    scrollTabToCenter(tabRefs.current[tabKey]);
  }, [selectedTab, tabNavigationList, scrollTabToCenter]);

  return (
    <StyledTabbedWrapper
      className={clsx('container tabs-wrapper', {
        'show-left-arrow': showLeftArrow,
        'show-right-arrow': showRightArrow,
      })}
      $isNextGenSearchForm={isNextGenSearchForm}
      ref={containerRef}
    >
      <span id={POLARIS_TAB_TITLE_ID} className="sr-only">
        {pageTitle}
      </span>
      <p
        aria-hidden="true"
        className={clsx('tab-banner-title t-title-m', { 'tab-banner-title__AI-search': isConvoAIActive })}
      >
        <span ref={titleTextRef}>{pageTitle}</span>
      </p>
      {showLeftArrow && (
        <button
          type="button"
          className="tab-arrow left"
          data-testid="left-arrow"
          onClick={() => handleScroll('left')}
          aria-label="Scroll tabs left"
          tabIndex={-1}
        >
          <span className="icon-arrow-left" data-id="left-arrow"></span>
        </button>
      )}
      {showRightArrow && (
        <button
          type="button"
          className="tab-arrow right"
          data-testid="right-arrow"
          onClick={() => handleScroll('right')}
          aria-label="Scroll tabs right"
          tabIndex={-1}
        >
          <span className="icon-arrow-right" data-id="right-arrow"></span>
        </button>
      )}
      <StyledTabNavigation
        data-testid="tab-navigation"
        className={clsx('tab-navigation', {
          'tab-gradient-left': showLeftArrow && !showRightArrow,
          'tab-gradient-right': !showLeftArrow && showRightArrow,
          'tab-gradient-dual': showLeftArrow && showRightArrow,
        })}
        role="tablist"
        aria-labelledby={POLARIS_TAB_TITLE_ID}
        ref={navigationRef}
      >
        {tabNavigationList.map((tab, index) => {
          const { tabTitle, tabValue, tabIcon, tabTrackingDescription, customTabClickTrackValue } = tab;
          const isActive = isValueMode ? selectedTab === tabValue : selectedTab === tabTitle;
          const tabKey = tabValue || `tab-${index}`;
          return (
            <Button
              key={tabKey}
              ref={(el: HTMLButtonElement | null) => {
                tabRefs.current[tabKey] = el;
              }}
              id={tabValue}
              isLink={true}
              href={isValueMode ? `#${encodeURIComponent(tabValue)}` : `?tab=${encodeURIComponent(tabTitle)}`}
              custom_click_track_value={customTabClickTrackValue || tabTrackingDescription}
              ariaRole="tab"
              callback={e => {
                e.preventDefault(); // prevent default anchor navigation
                if (!isActive) setSelectedTab?.(isValueMode ? tabValue : tabTitle);
                scrollTabToCenter(tabRefs.current[tabKey]);
              }}
              buttonCopy={tabTitle}
              isTextBeforeChildren={false}
              aria-controls={`${isValueMode ? tabValue : tabTitle}-panel`}
              aria-selected={isActive ? 'true' : 'false'}
              className={clsx('tab-item m-icon-text', isActive && 'is-active')}
            >
              <span className={clsx('icon-m', tabIcon)}></span>
            </Button>
          );
        })}
      </StyledTabNavigation>
      {children}
    </StyledTabbedWrapper>
  );
};
